import {FC, useMemo, useState} from 'react';
import {Formik} from 'formik';
import {Box, Tooltip, Typography} from '@mui/material';
import Button from '@components/Button';
import PaymentOptions from '@/components/Organization/Auction/AddTableTicket/PaymentTicketsInfo/PaymentOptions';
import {COVER_FEE_TEXT, H6, TOP} from '@/utils/constants';
import {IRaffleTicketsData} from '@/utils/types';
import {ReactComponent as QuestionIcon} from '@icons/question.svg';
import RaffleTicketItem from '../RaffleTicketItem';
import {raffle} from '../raffle.styles';
import {raffleBuyTicketModal} from './raffleBuyTicketModal.style';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {RaffleService} from '@/utils/services/raffle';
import {useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {getUserSelector} from '@/store/user';
import {PAYMENT_METHOD} from '@/utils/enums';
import {getRaffleListSelector} from '@/store/raffle/selectors';
import Checkbox from '@/shared/components/Checkbox';
import {calculateFees} from '@/utils/helpers/feeCalculations';
import {currencyFormatter} from '@/utils/helpers/currency';

interface RaffleBuyTicketModalProps {
  onCloseModal: () => void;
  totalPrice: number;
  raffleData: IRaffleTicketsData[];
}

const RaffleBuyTicketModal: FC<RaffleBuyTicketModalProps> = ({onCloseModal, totalPrice, raffleData}) => {
  const user = useAppSelector(getUserSelector);
  const currentEvent = useAppSelector(getCurrentEventSelector);
  const raffleTicketsData = useAppSelector(getRaffleListSelector);

  const [isCoverFee, setIsCoverFee] = useState(true);

  const handlePaymentSubmit = async (formValues: {paymentOption: string}) => {
    if (!currentEvent?.id || !user?.bidderId) {
      toast.error('Missing required user or event data');
      return;
    }

    const selectedBundle = raffleData.find((item: IRaffleTicketsData) => item.amount > 0);

    if (!selectedBundle) {
      toast.error('Please select at least one raffle ticket bundle');
      return;
    }
    try {
      const reqBody = {
        attendeeId: user.bidderId,
        raffleBundleId: selectedBundle.id!,
        amount: selectedBundle.amount,
        isCoverFee,
      };

      const {data} = await RaffleService.buy({eventId: currentEvent.id, reqBody});
      if (data?.url) {
        window.location.href = data.url;
      }
      onCloseModal();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  const fees = useMemo(
    () => calculateFees(+totalPrice, isCoverFee, currentEvent?.organization?.paymentSettings?.transactionFee ?? 0),
    [totalPrice, isCoverFee, currentEvent?.organization?.paymentSettings?.transactionFee]
  );

  return (
    <Box sx={raffleBuyTicketModal.content}>
      <Formik initialValues={{paymentOption: PAYMENT_METHOD.CREDIT_CARD}} onSubmit={handlePaymentSubmit}>
        {({submitForm}) => (
          <>
            <Box sx={raffleBuyTicketModal.tiersWrap}>
              <Typography variant={H6} sx={raffleBuyTicketModal.title}>
                Buy Raffle Tickets
              </Typography>

              <Box sx={raffleBuyTicketModal.contentWrap}>
                <Typography sx={raffleBuyTicketModal.listTitle}>Raffle tickets:</Typography>
                <Box sx={raffle.tableHead}>
                  <Typography sx={raffle.columnLabel}>Price</Typography>
                  <Typography sx={raffle.columnLabel}>Quantity</Typography>
                  <Typography sx={raffle.columnLabel}>Subtotal</Typography>
                </Box>
                {raffleData.map((tier: IRaffleTicketsData) => (
                  <Box key={tier.price} sx={raffle.ticketRow}>
                    <RaffleTicketItem tier={tier} raffleData={raffleData} />
                  </Box>
                ))}

                <Box sx={raffleBuyTicketModal.feeWrap}>
                  <Box sx={raffleBuyTicketModal.feeLabel}>
                    <Checkbox value={isCoverFee} onChange={() => setIsCoverFee(!isCoverFee)} />
                    <Typography>Cover fee</Typography>
                    <Tooltip title={`${COVER_FEE_TEXT} info`} placement={TOP}>
                      <Box sx={raffle.coverFeeTooltip}>
                        <QuestionIcon />
                      </Box>
                    </Tooltip>
                  </Box>
                  <Typography>
                    {isCoverFee ? currencyFormatter.format(fees.coverFeeLine) : currencyFormatter.format(0)}
                  </Typography>
                </Box>

                <Box sx={raffle.totalWrap}>
                  <Typography sx={raffle.totalText}>Total</Typography>
                  <Typography sx={raffle.totalText}>{currencyFormatter.format(fees.amountChargedToPatron)}</Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={raffleBuyTicketModal.paymentWrap}>
              <PaymentOptions />
            </Box>

            <Box sx={raffleBuyTicketModal.btnWrap}>
              <Button variant="outline" sx={raffleBuyTicketModal.btn} onClick={onCloseModal} title="Cancel" />

              <Button sx={raffleBuyTicketModal.btn} onClick={submitForm} title="Confirm" />
            </Box>
          </>
        )}
      </Formik>
    </Box>
  );
};

export default RaffleBuyTicketModal;
